import React, { useState, useEffect } from "react";
import axios from "axios";
import { Edit, Trash2, Eye } from "lucide-react";
import "./AllRole.css";

const API_BASE_URL = "https://server.tradefxonline.com";

const AllRole = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // 'create' or 'edit'
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    access_type: "custom",
    permissions: [],
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRole, setViewingRole] = useState(null);

  // Fetch all roles
  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/admin_fetch_roles.php`);
      if (response.data.success) {
        setRoles(response.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching roles:", err);
      setError("Failed to fetch roles");
    } finally {
      setLoading(false);
    }
  };

  // Fetch all permissions
  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin_get_all_permissions.php`);
      if (response.data.success) {
        setPermissions(response.data.permissions || []);
      }
    } catch (err) {
      console.error("Error fetching permissions:", err);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  // Open create modal
  const handleCreateNew = () => {
    setModalMode("create");
    setFormData({ name: "", description: "", access_type: "custom", permissions: [] });
    setSelectedRole(null);
    setError("");
    setSuccess("");
    setShowModal(true);
  };

  // Open edit modal
  const handleEdit = (role) => {
    setModalMode("edit");
    setSelectedRole(role);
    
    // Extract permission IDs from the permissions array
    let permissionIds = [];
    if (role.access_type === "custom" && Array.isArray(role.permissions)) {
      permissionIds = role.permissions.map((p) => p.id);
    }
    
    setFormData({
      name: role.name,
      description: role.description,
      access_type: role.access_type,
      permissions: permissionIds,
    });
    setError("");
    setSuccess("");
    setShowModal(true);
  };

  // Handle form submit (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validation for custom roles
    if (formData.access_type === "custom" && formData.permissions.length === 0) {
      setError("Please select at least one permission for custom role");
      return;
    }

    try {
      if (modalMode === "create") {
        // Create new role
        const payload = {
          name: formData.name,
          description: formData.description,
          access_type: formData.access_type,
        };

        // Add permissions only for custom roles
        if (formData.access_type === "custom") {
          payload.permissions = formData.permissions;
        }

        const response = await axios.post(
          `${API_BASE_URL}/admin_add_roles.php`,
          payload,
          { headers: { "Content-Type": "application/json" } }
        );

        if (response.data.success) {
          setSuccess("Role added successfully!");
          setShowModal(false);
          fetchRoles();
        } else {
          setError(response.data.message || "Failed to create role");
        }
      } else {
        // Update existing role
        const payload = {
          role_id: selectedRole.id,
          name: formData.name,
          description: formData.description,
          access_type: formData.access_type,
        };

        // Add permissions only for custom roles
        if (formData.access_type === "custom") {
          payload.permissions = formData.permissions;
        }

        const response = await axios.post(
          `${API_BASE_URL}/admin_update_roles.php`,
          payload,
          { headers: { "Content-Type": "application/json" } }
        );

        if (response.data.success) {
          setSuccess("Role updated successfully!");
          setShowModal(false);
          fetchRoles();
        } else {
          setError(response.data.message || "Failed to update role");
        }
      }
    } catch (err) {
      console.error("Error saving role:", err);
      setError(err.response?.data?.message || "An error occurred");
    }
  };

  // Delete role
  const handleDelete = async (roleId) => {
    if (!window.confirm("Are you sure you want to delete this role?")) {
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/admin_delete_roles.php`,
        { role_id: roleId },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.success) {
        setSuccess("Role deleted successfully!");
        fetchRoles();
        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError(response.data.message || "Failed to delete role");
        setTimeout(() => setError(""), 3000);
      }
    } catch (err) {
      console.error("Error deleting role:", err);
      setError("An error occurred while deleting the role");
      setTimeout(() => setError(""), 3000);
    }
  };

  // Filter roles based on search
  const filteredRoles = roles.filter(
    (role) =>
      role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle permission checkbox change
  const handlePermissionToggle = (permissionId) => {
    setFormData((prev) => {
      const isSelected = prev.permissions.includes(permissionId);
      return {
        ...prev,
        permissions: isSelected
          ? prev.permissions.filter((id) => id !== permissionId)
          : [...prev.permissions, permissionId],
      };
    });
  };

  // Handle select all permissions for a module
  const handleModuleToggle = (moduleName) => {
    const modulePermissions = permissions
      .filter((p) => p.module === moduleName)
      .map((p) => p.id);

    const allSelected = modulePermissions.every((id) =>
      formData.permissions.includes(id)
    );

    setFormData((prev) => {
      if (allSelected) {
        // Deselect all module permissions
        return {
          ...prev,
          permissions: prev.permissions.filter(
            (id) => !modulePermissions.includes(id)
          ),
        };
      } else {
        // Select all module permissions
        const newPermissions = [...new Set([...prev.permissions, ...modulePermissions])];
        return {
          ...prev,
          permissions: newPermissions,
        };
      }
    });
  };

  // Group permissions by module
  const groupedPermissions = permissions.reduce((acc, permission) => {
    if (!acc[permission.module]) {
      acc[permission.module] = [];
    }
    acc[permission.module].push(permission);
    return acc;
  }, {});

  // Handle access type change
  const handleAccessTypeChange = (value) => {
    setFormData({
      ...formData,
      access_type: value,
      permissions: value === "full" ? [] : formData.permissions,
    });
  };

  // Handle view permissions
  const handleViewPermissions = (role) => {
    setViewingRole(role);
    setShowViewModal(true);
  };

  return (
    <div className="main-content">
      <div className="breadcrumb">
        Home / <span>View All</span>
      </div>
      <h2>Dashboard Role List</h2>

      {success && (
        <div className="alert alert-success">{success}</div>
      )}
      {error && !showModal && (
        <div className="alert alert-error">{error}</div>
      )}

      <div className="page-header">
        <div>
          <h2>View all dashboard roles</h2>
        </div>
        <div className="header-buttons">
          <button className="btn blue" onClick={handleCreateNew}>
            + Create New
          </button>
        </div>
      </div>

      <div className="card">
        <div className="top-controls">
          <div className="search-box">
            Search:{" "}
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or description..."
            />
          </div>
        </div>

     

        {loading ? (
          <div className="loading-state">Loading roles...</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Role Name</th>
                <th>Description</th>
                {/* <th>Access Type</th> */}
                <th>Permissions</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRoles.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty">
                    No data available in table
                  </td>
                </tr>
              ) : (
                filteredRoles.map((role, index) => {
                  // Get permissions display
                  let permissionsDisplay = "-";
                  if (role.access_type === "full") {
                    permissionsDisplay = "All permissions granted";
                  } else if (Array.isArray(role.permissions) && role.permissions.length > 0) {
                    permissionsDisplay = `${role.permissions.length} permission${role.permissions.length > 1 ? 's' : ''}`;
                  } else if (role.access_type === "custom") {
                    permissionsDisplay = "No permissions";
                  }
                  
                  return (
                    <tr key={role.id}>
                      <td>{index + 1}</td>
                      <td>{role.name}</td>
                      <td>{role.description}</td>
                      <td>
                        <span className={`badge ${role.access_type}`}>
                          {role.access_type}
                        </span>
                      </td>
                      <td>
                        <span 
                          className="permissions-count"
                          onClick={() => handleViewPermissions(role)}
                          title="Click to view permissions"
                          style={{ cursor: 'pointer' }}
                        >
                          {permissionsDisplay}
                        </span>
                      </td>
                      <td>
                        {new Date(role.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <button
                          className="action-btn edit"
                          onClick={() => handleEdit(role)}
                          title="Edit"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          className="action-btn delete"
                          onClick={() => handleDelete(role.id)}
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                        <button
                          className="action-btn view"
                          onClick={() => handleViewPermissions(role)}
                          title="View Permissions"
                        >
                          <Eye size={18} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}

        <div className="table-footer">
          <p>
            Showing {filteredRoles.length > 0 ? 1 : 0} to{" "}
            {filteredRoles.length} of {filteredRoles.length} entries
          </p>
        </div>
      </div>

      {/* Modal for Create/Edit */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modalMode === "create" ? "Create New Role" : "Edit Role"}</h3>
              <button
                className="close-btn"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">
                  Role Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                  placeholder="Enter role name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">
                  Description <span className="required">*</span>
                </label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  required
                  placeholder="Enter role description"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label htmlFor="access_type">
                  Access Type <span className="required">*</span>
                </label>
                <select
                  id="access_type"
                  value={formData.access_type}
                  onChange={(e) => handleAccessTypeChange(e.target.value)}
                  required
                >
                  <option value="custom">Custom</option>
                  <option value="full">Full Access (Super Admin)</option>
                </select>
                {formData.access_type === "full" && (
                  <small style={{ color: "#6b7280", marginTop: "5px", display: "block" }}>
                    Full access grants all permissions automatically
                  </small>
                )}
              </div>

              {/* Permissions Section - Only show for custom roles */}
              {formData.access_type === "custom" && (
                <div className="form-group">
                  <label>
                    Permissions <span className="required">*</span>
                  </label>
                  <div className="permissions-container">
                    {Object.keys(groupedPermissions).sort().map((module) => (
                      <div key={module} className="permission-module">
                        <div className="module-header">
                          <label className="checkbox-label module-checkbox">
                            <input
                              type="checkbox"
                              checked={groupedPermissions[module].every((p) =>
                                formData.permissions.includes(p.id)
                              )}
                              onChange={() => handleModuleToggle(module)}
                            />
                            <strong>{module}</strong>
                          </label>
                        </div>
                        <div className="permission-list">
                          {groupedPermissions[module].map((permission) => (
                            <label
                              key={permission.id}
                              className="checkbox-label permission-item"
                            >
                              <input
                                type="checkbox"
                                checked={formData.permissions.includes(
                                  permission.id
                                )}
                                onChange={() =>
                                  handlePermissionToggle(permission.id)
                                }
                              />
                              <span>
                                {permission.name}
                                <small className="permission-desc">
                                  {permission.description}
                                </small>
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {formData.permissions.length === 0 && (
                    <small style={{ color: "#ef4444", marginTop: "5px", display: "block" }}>
                      Please select at least one permission
                    </small>
                  )}
                </div>
              )}

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn blue">
                  {modalMode === "create" ? "Create Role" : "Update Role"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Permissions Modal */}
      {showViewModal && viewingRole && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal-content view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Permissions for "{viewingRole.name}"</h3>
              <button
                className="close-btn"
                onClick={() => setShowViewModal(false)}
              >
                ×
              </button>
            </div>

            <div style={{ padding: "20px" }}>
              {viewingRole.access_type === "full" ? (
                <div className="full-access-notice">
                  <strong>Full Access Role</strong>
                  <p>This role has access to all permissions in the system.</p>
                </div>
              ) : Array.isArray(viewingRole.permissions) && viewingRole.permissions.length > 0 ? (
                <div className="permissions-list-view">
                  {viewingRole.permissions.map((permission) => (
                    <div key={permission.id} className="permission-item-view">
                      <div className="permission-header-view">
                        <span className="permission-name-view">{permission.name}</span>
                        <span className="permission-module-badge">{permission.module}</span>
                      </div>
                      <p className="permission-desc-view">{permission.description}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-permissions-notice">
                  <p>No permissions assigned to this role.</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn secondary"
                onClick={() => setShowViewModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

     
    </div>
  );
};

export default AllRole;
